'use client';

import { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import Layout from '../Layout';
import TeamPage from './Team';
import Integrations from '../Integrations/Integrations';
import Billing from './Billing';
import Account from './Account';
import { LockClosedIcon } from '@heroicons/react/24/outline';

export default function SettingsPage() {
  const { user } = useContext(AuthContext);
  const { tab } = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);

  const validTabs = ['Account', 'Team', 'Integrations', 'Billing'];
  const activeTab = validTabs.includes(tab) ? tab : 'Account';

  useEffect(() => {
    if (!validTabs.includes(tab)) {
      navigate('/settings/Account', { replace: true });
    }
  }, [tab, navigate]);

  useEffect(() => {
    document.title = "Settings | TransTech Connect";
    return () => {
      document.title = "TransTech Connect";
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const fetchOrganization = async () => {
      try {
        const { data, error } = await supabase
          .from('organization_members')
          .select('organizations(id, name), role')
          .eq('user_id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching organization:', error);
        } else {
          setOrganization(data?.organizations || null);
          setUserRole(data?.role || null);
        }
      } catch (err) {
        console.error('Error fetching organization:', err);
      }
    };

    fetchOrganization();
  }, [user]);

  const handleTabChange = (tabName) => {
    if (
      (tabName === 'Integrations' || tabName === 'Billing') &&
      (!organization || (userRole !== 'admin' && userRole !== 'owner'))
    ) {
      return;
    }
    navigate(`/settings/${tabName}`);
  };

  return (
    <Layout>
      <div>
        <h3 className="text-base font-semibold text-gray-900">Settings</h3>

        {/* Tabs */}
        <div className="border-b border-gray-200 mt-4">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {validTabs.map((tabName) => {
              const isDisabled = (tabName === 'Integrations' || tabName === 'Billing') &&
                                (!organization || (userRole !== 'admin' && userRole !== 'owner'));
              return (
                <div key={tabName} className="relative group">
                  <button
                    onClick={() => handleTabChange(tabName)}
                    disabled={isDisabled}
                    className={classNames(
                      activeTab === tabName && !isDisabled
                        ? 'border-yellow-700 text-yellow-700'
                        : isDisabled
                        ? 'border-transparent text-gray-400 cursor-not-allowed'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
                      'whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium flex items-center'
                    )}
                  >
                    {tabName}
                    {isDisabled && (
                      <LockClosedIcon
                        className="ml-2 h-4 w-4 text-gray-400"
                        aria-hidden="true"
                      />
                    )}
                  </button>
                  {isDisabled && (
                    <div className="absolute bottom-full text-center mt-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 shadow-md">
                      Admins Only
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === 'Team' && <TeamPage organization={organization} user={user} />}
          {activeTab === 'Integrations' && organization && (userRole === 'admin' || userRole === 'owner') && <Integrations />}
          {activeTab === 'Account' && <Account user={user} organization={organization} />}
          {activeTab === 'Billing' && organization && (userRole === 'admin' || userRole === 'owner') && <Billing organization={organization} />}
        </div>
      </div>
    </Layout>
  );
}

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}